import * as XLSX from "xlsx";

const EXCLUDED_SHEETS = new Set([
  "Cover", "Calendar", "Bus_Time", "Counter Pick", "Copy of Counter Pick",
  "RPL", "RPL_Stats", "RPL_Winter", "RPL_Stat", "RPL_Scrim",
  "APL", "APL_Stats", "FS_Ban_Pick", "player_stats", "Player_Stats",
  "Top4", "Top4_Stats", "Specter_Scrim", "Specter_Stats", "Sheet60",
  "RPL_Ban_Pick", "Gold_Stats", "KDA_Stats", "Weekly_Stats", "AVG_Death_Stats",
  "Timeline_Stats",
  "RPL_player_stats", "ComboStats", "Combo_Stats", "Competitor_Hero_stats", "WinRateScrim",
  "Munez_VS_Lycan", "Sheet51", "Team List", "Hero List", "FS Line up"
]);
const FS_SCRIM_SHEETS = new Set(["All_Scrim_Stats", "All_Scrim", "APL_Scrim", "Girl_Scrim"]);
const FS_TEAM_CODE = "FS";
const FS_SCRIM_VISIBLE_START_ROW = 1230; // 1-based Excel row

const ROLE_MAP = {
  ROAMING: "SUP",
  SUPPORT: "SUP",
  SUP: "SUP",
  JUNGLE: "JUG",
  JUG: "JUG",
  MID: "MID",
  MIDLANE: "MID",
  ADL: "ADL",
  DSL: "DSL",
};
const ROLE_KEYS = ["DSL", "JUG", "MID", "ADL", "SUP"];

const OFFICIAL_KEYWORDS = ["rpl", "apl"];
const SCRIM_KEYWORDS = ["scrim"];

function norm(v) {
  return String(v ?? "").trim().toLowerCase();
}

function isFsScrimSheetName(sheetName) {
  const key = String(sheetName || "").trim().toLowerCase();
  return [...FS_SCRIM_SHEETS].some((name) => name.toLowerCase() === key);
}

function constrainRowsToVisibleFsScrim(rows, sheetName) {
  const key = String(sheetName || "").trim().toLowerCase();
  if (key !== "all_scrim_stats" && key !== "all_scrim") return rows;
  if (!Array.isArray(rows) || rows.length <= 1) return rows || [];
  const isHeaderLike = (row) => {
    const h = (row || []).map((c) => norm(c));
    const hasCore = h.includes("week") && h.includes("game") && h.includes("competitor");
    const hasDraft = h.some((c) => c === "match win" || c === "type" || c === "fs pick 1");
    return hasCore && hasDraft;
  };
  if (isHeaderLike(rows[0])) return rows;
  for (let i = 1; i < Math.min(rows.length, 40); i++) {
    if (isHeaderLike(rows[i])) {
      const header = rows[i];
      return [header, ...rows.slice(i + 1)];
    }
  }
  const header = rows[0];
  const startIdx = Math.max(1, FS_SCRIM_VISIBLE_START_ROW - 1);
  return [header, ...rows.slice(startIdx)];
}

function normTeamCode(v) {
  return String(v ?? "").trim().toUpperCase();
}

function normalizeTeamAlias(v) {
  const t = normTeamCode(v);
  const alias = {
    TLN: "TEN",
    EAG: "EA",
    HDG: "HD",
  };
  return alias[t] || t;
}

function safeNum(v, fallback = 0) {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
}

function pct(n, d) {
  return d > 0 ? +((n * 100) / d).toFixed(2) : 0;
}

function parseExcelSerialToYMD(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return "";
  const parsed = XLSX.SSF?.parse_date_code ? XLSX.SSF.parse_date_code(n, { date1904: false }) : null;
  if (!parsed || !parsed.y || !parsed.m || !parsed.d) return "";
  const y = String(parsed.y).padStart(4, "0");
  const m = String(parsed.m).padStart(2, "0");
  const d = String(parsed.d).padStart(2, "0");
  return `${y}${m}${d}`;
}

function parseDateToYMD(v) {
  if (v == null || v === "") return "";
  if (v instanceof Date && Number.isFinite(v.getTime())) {
    const y = String(v.getFullYear()).padStart(4, "0");
    const m = String(v.getMonth() + 1).padStart(2, "0");
    const d = String(v.getDate()).padStart(2, "0");
    return `${y}${m}${d}`;
  }

  const s = String(v).trim();
  if (/^\d{8}$/.test(s)) return s;
  {
    const dmY = s.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/);
    if (dmY) {
      const d = +dmY[1];
      const m = +dmY[2];
      const y = +dmY[3];
      if (y >= 2000 && y <= 2100 && m >= 1 && m <= 12 && d >= 1 && d <= 31) {
        return `${String(y).padStart(4, "0")}${String(m).padStart(2, "0")}${String(d).padStart(2, "0")}`;
      }
    }
  }
  {
    const yMd = s.match(/^(\d{4})[/-](\d{1,2})[/-](\d{1,2})$/);
    if (yMd) {
      const y = +yMd[1];
      const m = +yMd[2];
      const d = +yMd[3];
      if (y >= 2000 && y <= 2100 && m >= 1 && m <= 12 && d >= 1 && d <= 31) {
        return `${String(y).padStart(4, "0")}${String(m).padStart(2, "0")}${String(d).padStart(2, "0")}`;
      }
    }
  }

  const n = Number(s);
  if (Number.isFinite(n)) {
    if (n >= 20000101 && n <= 21001231) return String(Math.trunc(n));
    if (n >= 20000 && n <= 70000) return parseExcelSerialToYMD(n);
  }

  return "";
}

function normalizeRole(v) {
  const k = String(v ?? "").trim().toUpperCase();
  return ROLE_MAP[k] || k || "";
}

function detectRoleFromHeaderName(name) {
  const n = String(name || "").trim().toUpperCase().replace(/\s+/g, " ");
  if (!n) return "";
  if (/(^|\s)(DSL|DARK SLAYER|SLAYER)(\s|$)/.test(n)) return "DSL";
  if (/(^|\s)(JUG|JUNGLE|JG)(\s|$)/.test(n)) return "JUG";
  if (/(^|\s)(MID|MIDLANE)(\s|$)/.test(n)) return "MID";
  if (/(^|\s)(ADL|ADC|AD CARRY|CARRY)(\s|$)/.test(n)) return "ADL";
  if (/(^|\s)(SUP|SUPPORT|ROAMING|ROAM)(\s|$)/.test(n)) return "SUP";
  return "";
}

function detectTeamRoleMetricCols(header) {
  const cols = {
    gpm: { DSL: -1, JUG: -1, MID: -1, ADL: -1, SUP: -1 },
    dpm: { DSL: -1, JUG: -1, MID: -1, ADL: -1, SUP: -1 },
    kill: { DSL: -1, JUG: -1, MID: -1, ADL: -1, SUP: -1 },
    death: { DSL: -1, JUG: -1, MID: -1, ADL: -1, SUP: -1 },
    assist: { DSL: -1, JUG: -1, MID: -1, ADL: -1, SUP: -1 },
  };

  for (let i = 0; i < (header || []).length; i++) {
    const raw = String(header[i] || "");
    const c = norm(raw);
    if (!c) continue;
    const role = detectRoleFromHeaderName(raw);
    if (!role) continue;
    if (/(\bop\b|\bopp\b|\benemy\b|^b\s|^op\s|^op_)/i.test(c)) continue;

    let metric = "";
    if (c.includes("dmg/min") || c.includes("damage/min")) metric = "dpm";
    else if ((/\bgpm\b/).test(c)) metric = "gpm";
    else if ((/\bkill\b/).test(c)) metric = "kill";
    else if ((/\bdeath\b/).test(c)) metric = "death";
    else if ((/\bassist\b/).test(c)) metric = "assist";
    if (!metric) continue;

    if (cols[metric][role] < 0) cols[metric][role] = i;
  }

  return cols;
}

function toStatNum(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function parseFirstBloodWon(v, teamCode = "") {
  const n = Number(v);
  if (Number.isFinite(n)) {
    if (n === 1) return true;
    if (n === 0) return false;
  }

  const s = String(v ?? "").trim().toUpperCase();
  if (!s) return null;
  if (["FS", "A", "WIN", "W", "TRUE", "YES"].includes(s)) return true;
  if (["OP", "B", "LOSE", "L", "FALSE", "NO"].includes(s)) return false;
  if (teamCode && s === String(teamCode).trim().toUpperCase()) return true;
  if (/\bOP\b|\bOPP\b|\bENEMY\b/.test(s)) return false;
  return null;
}

function bucketFromType(typeRaw) {
  const t = norm(typeRaw);
  if (!t) return "other";
  if (SCRIM_KEYWORDS.some((k) => t.includes(k))) return "scrim";
  if (OFFICIAL_KEYWORDS.some((k) => t.includes(k))) return "official";
  return "other";
}

function findHeaderRow(rows) {
  let bestRow = -1;
  let bestScore = -1;

  for (let r = 0; r < Math.min(rows.length, 20); r++) {
    const row = (rows[r] || []).map((c) => norm(c));
    const score =
      (row.includes("date") ? 2 : 0) +
      (row.includes("week") ? 1 : 0) +
      (row.includes("game") ? 1 : 0) +
      (row.includes("competitor") ? 2 : 0) +
      (row.some((c) => c.includes("match win")) ? 2 : 0) +
      (row.some((c) => c.includes("side")) ? 1 : 0);

    if (score > bestScore) {
      bestScore = score;
      bestRow = r;
    }
  }

  return bestScore >= 5 ? bestRow : -1;
}

function sortedUnique(arr) {
  return [...new Set(arr)].sort((a, b) => a - b);
}

function indexOfFirst(header, matcher) {
  for (let i = 0; i < header.length; i++) {
    if (matcher(norm(header[i]))) return i;
  }
  return -1;
}

function detectIndexMap(header) {
  const h = header || [];

  const map = {
    date: indexOfFirst(h, (c) => c === "date"),
    week: indexOfFirst(h, (c) => c === "week"),
    game: indexOfFirst(h, (c) => c === "game"),
    competitor: indexOfFirst(h, (c) => c === "competitor" || c === "opponent"),
    side: indexOfFirst(h, (c) => c.endsWith(" side") || c === "side"),
    matchWin: indexOfFirst(h, (c) => c.includes("match win") || c === "result"),
    teamWin: indexOfFirst(h, (c) => c.endsWith(" win") && !c.startsWith("op ")),
    type: indexOfFirst(h, (c) => c === "type"),
    endType: indexOfFirst(h, (c) => c.startsWith("end")),
    firstBlood: indexOfFirst(h, (c) => c === "first blood" || c === "first kill"),
  };

  const opBan = [];
  const opPick = [];
  const opPos = [];
  const teamBan = [];
  const teamPick = [];
  const teamPos = [];
  const teamRoleCols = { DSL: -1, JUG: -1, MID: -1, ADL: -1, SUP: -1 };
  const opRoleCols = { DSL: -1, JUG: -1, MID: -1, ADL: -1, SUP: -1 };

  for (let i = 0; i < h.length; i++) {
    const c = norm(h[i]);
    if (!c) continue;

    if ((/^op\s*ban\s*\d+$/).test(c) || (/^b\s*ban\s*\d+$/).test(c)) opBan.push(i);
    if ((/^op\s*pick\s*\d+$/).test(c) || (/^b\s*pick\s*\d+$/).test(c) || (/^op\s*p\d+$/).test(c)) opPick.push(i);
    if ((/^op\s*position\s*\d+$/).test(c) || (/^b\s*position\s*\d+$/).test(c) || (/^op\s*po\d+$/).test(c)) opPos.push(i);

    const isTeamBan = c.includes("ban") && !c.startsWith("op ") && !c.startsWith("b ") && !c.includes("match");
    if (isTeamBan) teamBan.push(i);

    const isTeamPick =
      !c.startsWith("op ") &&
      !c.startsWith("b ") &&
      ((/\bp\d+$/).test(c) || (/\bpick\s*\d+$/).test(c));
    if (isTeamPick) teamPick.push(i);

    const isTeamPos = !c.startsWith("op ") && !c.startsWith("b ") && ((/\bpo\d+$/).test(c) || (/\bposition\s*\d+$/).test(c));
    if (isTeamPos) teamPos.push(i);

    if (!c.startsWith("op ")) {
      if (c.endsWith(" dsl") || c === "dsl") teamRoleCols.DSL = teamRoleCols.DSL >= 0 ? teamRoleCols.DSL : i;
      if (c.endsWith(" jug") || c.endsWith(" jungle") || c === "jug" || c === "jungle") teamRoleCols.JUG = teamRoleCols.JUG >= 0 ? teamRoleCols.JUG : i;
      if (c.endsWith(" mid") || c === "mid") teamRoleCols.MID = teamRoleCols.MID >= 0 ? teamRoleCols.MID : i;
      if (c.endsWith(" adl") || c === "adl") teamRoleCols.ADL = teamRoleCols.ADL >= 0 ? teamRoleCols.ADL : i;
      if (c.endsWith(" sup") || c.endsWith(" roaming") || c === "sup" || c === "roaming") teamRoleCols.SUP = teamRoleCols.SUP >= 0 ? teamRoleCols.SUP : i;
    }
    if (c.startsWith("op ")) {
      if (c.endsWith("dsl")) opRoleCols.DSL = opRoleCols.DSL >= 0 ? opRoleCols.DSL : i;
      if (c.endsWith("jug") || c.endsWith("jungle")) opRoleCols.JUG = opRoleCols.JUG >= 0 ? opRoleCols.JUG : i;
      if (c.endsWith("mid")) opRoleCols.MID = opRoleCols.MID >= 0 ? opRoleCols.MID : i;
      if (c.endsWith("adl")) opRoleCols.ADL = opRoleCols.ADL >= 0 ? opRoleCols.ADL : i;
      if (c.endsWith("sup") || c.endsWith("roaming")) opRoleCols.SUP = opRoleCols.SUP >= 0 ? opRoleCols.SUP : i;
    }
  }

  map.opBan = sortedUnique(opBan).slice(0, 4);
  map.opPick = sortedUnique(opPick).slice(0, 5);
  map.opPos = sortedUnique(opPos).slice(0, 5);
  map.teamBan = sortedUnique(teamBan).slice(0, 4);
  map.teamPick = sortedUnique(teamPick).slice(0, 5);
  map.teamPos = sortedUnique(teamPos).slice(0, 5);
  map.teamRoleCols = teamRoleCols;
  map.opRoleCols = opRoleCols;

  return map;
}

function inferDateColFromRows(rows, startRow = 1, maxScanRows = 300) {
  const sample = (rows || []).slice(startRow, startRow + maxScanRows);
  const maxCols = Math.max(
    (rows?.[startRow - 1] || []).length || 0,
    ...sample.map((r) => (r || []).length || 0),
    0
  );
  let bestCol = -1;
  let bestScore = 0;
  for (let c = 0; c < maxCols; c++) {
    let score = 0;
    for (let i = 0; i < sample.length; i++) {
      if (parseDateToYMD(sample[i]?.[c])) score += 1;
    }
    if (score > bestScore) {
      bestScore = score;
      bestCol = c;
    }
  }
  return bestScore >= 3 ? bestCol : -1;
}

function sanitizeHero(v) {
  const s = String(v ?? "").trim();
  if (!s) return "";
  const low = s.toLowerCase();
  if (
    low === "nan" ||
    low === "#n/a" ||
    low === "#ref!" ||
    low.includes("#div/0") ||
    low.includes("sum") ||
    low.includes("total") ||
    low.includes("counta") ||
    low.includes("pick rate") ||
    low.includes("win rate") ||
    low.includes("kda") ||
    s.includes("ผลรวม")
  ) return "";
  return s;
}

function resolveTeamWin(matchWinRaw, teamCode) {
  const n = Number(matchWinRaw);
  if (Number.isFinite(n) && (n === 0 || n === 1)) return n === 1;

  const mw = String(matchWinRaw ?? "").trim();
  if (!mw) return null;

  const up = mw.toUpperCase();
  const t = String(teamCode || "").trim().toUpperCase();
  if (!t) return null;

  if (up === t) return true;
  if (["WIN", "W"].includes(up)) return true;
  if (["LOSE", "L"].includes(up)) return false;
  return false;
}

function readStrictTypeFromRow(row) {
  for (const v of row || []) {
    const t = norm(v);
    if (t === "scrim") return "Scrim";
    if (t === "rpl") return "RPL";
  }
  return "";
}

function inferCompetitorFromRow(row, teamCode, knownTeams = new Set()) {
  const team = normalizeTeamAlias(teamCode);
  const stop = new Set(["SCRIM", "RPL", "APL", "AIC", "SEAGAME", "BLUE", "RED", "FS", "WIN", "LOSE"]);
  const roleStop = new Set(["DSL", "JUG", "JUNGLE", "MID", "ADL", "SUP", "ROAMING", "SUPPORT"]);

  // เลือกจาก known team code ก่อน เพื่อกันหยิบชื่อฮีโร่ผิด
  for (const v of row || []) {
    const up = normalizeTeamAlias(v);
    if (!up || up === team) continue;
    if (knownTeams.has(up)) return up;
  }

  for (const v of row || []) {
    const s = String(v ?? "").trim();
    if (!s) continue;
    const up = normalizeTeamAlias(s);
    if (up === team || stop.has(up) || roleStop.has(up)) continue;
    if (/^[A-Z0-9_&-]{2,10}$/.test(up)) return up;
  }
  return "";
}

function getTeamCodeFromSheet(sheetName) {
  if (isFsScrimSheetName(sheetName)) {
    return FS_TEAM_CODE;
  }
  return normalizeTeamAlias(String(sheetName || "").replace(/_ban_pick$/i, ""));
}

function getTeamCodesFromTeamListSheet(workbook) {
  const set = new Set();
  const ws = workbook?.Sheets?.["Team List"];
  if (!ws) return set;
  const rows = XLSX.utils.sheet_to_json(ws, { header: 1, defval: "" });
  for (let i = 1; i < Math.min(rows.length, 300); i++) {
    const t = normalizeTeamAlias(rows[i]?.[0]);
    if (t) set.add(t);
  }
  return set;
}

function collectKnownTeamCodes(workbook, teamSheets = []) {
  const set = new Set();

  (teamSheets || []).forEach((s) => set.add(getTeamCodeFromSheet(s)));

  // ดึงเพิ่มจาก Team List ถ้ามี
  const ws = workbook.Sheets["Team List"];
  if (ws) {
    const rows = XLSX.utils.sheet_to_json(ws, { header: 1, defval: "" });
    for (let i = 1; i < Math.min(rows.length, 300); i++) {
      const t = normalizeTeamAlias(rows[i]?.[0]);
      if (t) set.add(t);
    }
  }

  return set;
}

function _toWorkbook(input) {
  if (input && typeof input === "object" && Array.isArray(input.SheetNames) && input.Sheets) {
    return input;
  }
  if (typeof Blob !== "undefined" && input instanceof Blob) {
    throw new Error("parseTeamPerformanceFromWorkbook requires workbook/array buffer; use promise helper for Blob/File");
  }
  if (input instanceof ArrayBuffer) {
    return XLSX.read(new Uint8Array(input), { type: "array" });
  }
  if (input && (input.buffer instanceof ArrayBuffer || input instanceof Uint8Array)) {
    const u8 = input instanceof Uint8Array ? input : new Uint8Array(input);
    return XLSX.read(u8, { type: "array" });
  }
  throw new Error("Unsupported input type");
}

function detectTeamSheets(workbook, forceSheets = []) {
  if (forceSheets.length) return forceSheets.filter((s) => workbook.Sheets[s]);
  const teamCodesFromList = getTeamCodesFromTeamListSheet(workbook);

  return (workbook.SheetNames || []).filter((sheetName) => {
    if (EXCLUDED_SHEETS.has(sheetName)) return false;
    // ใช้เฉพาะชีทที่ "ชื่อชีท = ชื่อทีม" ตาม Team List
    if (teamCodesFromList.size > 0 && !isFsScrimSheetName(sheetName)) {
      const sheetTeamCode = normalizeTeamAlias(sheetName);
      if (!teamCodesFromList.has(sheetTeamCode)) return false;
    }

    const ws = workbook.Sheets[sheetName];
    if (!ws) return false;
    const rawRows = XLSX.utils.sheet_to_json(ws, { header: 1, defval: "" });
    const rows = constrainRowsToVisibleFsScrim(rawRows, sheetName);
    const hr = findHeaderRow(rows);
    if (hr < 0) return false;

    const map = detectIndexMap(rows[hr] || []);
    if (map.date < 0) map.date = inferDateColFromRows(rows, hr + 1);
    const roleColsCount = Object.values(map.teamRoleCols || {}).filter((i) => i >= 0).length;
    const hasPick = map.teamPick.length >= 4 || roleColsCount >= 4;
    const hasResult = map.matchWin >= 0 || map.teamWin >= 0;
    return map.date >= 0 && hasPick && hasResult;
  });
}

function parseOneTeamSheet(workbook, sheetName, knownTeams = new Set()) {
  const ws = workbook.Sheets[sheetName];
  if (!ws) return { rows: [], dropped: 0, parsed: 0, headerRow: -1 };

  const rawArr = XLSX.utils.sheet_to_json(ws, { header: 1, defval: "" });
  const arr = constrainRowsToVisibleFsScrim(rawArr, sheetName);
  const headerRow = findHeaderRow(arr);
  if (headerRow < 0) return { rows: [], dropped: 0, parsed: 0, headerRow: -1 };

  const idx = detectIndexMap(arr[headerRow] || []);
  if (idx.date < 0) idx.date = inferDateColFromRows(arr, headerRow + 1);
  const roleMetricCols = detectTeamRoleMetricCols(arr[headerRow] || []);
  const teamCode = getTeamCodeFromSheet(sheetName);

  const out = [];
  let dropped = 0;
  const sourceKind = isFsScrimSheetName(sheetName)
    ? "all_scrim_stats"
    : (/_ban_pick$/i.test(sheetName) ? "ban_pick" : "team_sheet");
  let droppedType = 0;
  let lastTypeRaw = "";

  for (let r = headerRow + 1; r < arr.length; r++) {
    const row = arr[r] || [];
    const typeCell = idx.type >= 0 ? row[idx.type] : "";
    const rowTypeRaw = String(typeCell ?? "").trim() || readStrictTypeFromRow(row);
    if (rowTypeRaw) lastTypeRaw = rowTypeRaw;

    const picksFromRoleCols = ["DSL", "JUG", "MID", "ADL", "SUP"]
      .map((rk) => {
        const c = idx.teamRoleCols?.[rk];
        return c >= 0 ? sanitizeHero(row[c]) : "";
      })
      .filter(Boolean);
    const picks = (idx.teamPick.length ? idx.teamPick.map((c) => sanitizeHero(row[c])) : picksFromRoleCols).filter(Boolean);
    const bans = idx.teamBan.map((c) => sanitizeHero(row[c])).filter(Boolean);
    const opPicksFromRoleCols = ["DSL", "JUG", "MID", "ADL", "SUP"]
      .map((rk) => {
        const c = idx.opRoleCols?.[rk];
        return c >= 0 ? sanitizeHero(row[c]) : "";
      })
      .filter(Boolean);
    const opPicks = (idx.opPick.length ? idx.opPick.map((c) => sanitizeHero(row[c])) : opPicksFromRoleCols).filter(Boolean);
    const opBans = idx.opBan.map((c) => sanitizeHero(row[c])).filter(Boolean);
    const positionsFromRoleCols = ["DSL", "JUG", "MID", "ADL", "SUP"].filter((rk) => (idx.teamRoleCols?.[rk] ?? -1) >= 0);
    const positions = (idx.teamPos.length ? idx.teamPos.map((c) => normalizeRole(row[c])) : positionsFromRoleCols).filter(Boolean);

    if (picks.length < 3 && bans.length === 0 && opPicks.length === 0) {
      dropped += 1;
      continue;
    }

    const typeRaw = rowTypeRaw || lastTypeRaw;
    if (!typeRaw) {
      dropped += 1;
      droppedType += 1;
      continue;
    }

    const winRaw = idx.teamWin >= 0 ? row[idx.teamWin] : (idx.matchWin >= 0 ? row[idx.matchWin] : "");
    const isWin = idx.teamWin >= 0 && Number.isFinite(Number(winRaw))
      ? Number(winRaw) === 1
      : resolveTeamWin(winRaw, teamCode);
    const ymd = idx.date >= 0 ? parseDateToYMD(row[idx.date]) : "";
    if (!ymd) {
      dropped += 1;
      continue;
    }
    const competitorRaw = idx.competitor >= 0 ? String(row[idx.competitor] ?? "").trim() : "";
    const competitor = competitorRaw
      ? normalizeTeamAlias(competitorRaw)
      : inferCompetitorFromRow(row, teamCode, knownTeams);
    const firstBloodWon = idx.firstBlood >= 0 ? parseFirstBloodWon(row[idx.firstBlood], teamCode) : null;
    const roleStats = {};
    ROLE_KEYS.forEach((rk) => {
      const stat = {
        gpm: roleMetricCols.gpm[rk] >= 0 ? toStatNum(row[roleMetricCols.gpm[rk]]) : null,
        dpm: roleMetricCols.dpm[rk] >= 0 ? toStatNum(row[roleMetricCols.dpm[rk]]) : null,
        kill: roleMetricCols.kill[rk] >= 0 ? toStatNum(row[roleMetricCols.kill[rk]]) : null,
        death: roleMetricCols.death[rk] >= 0 ? toStatNum(row[roleMetricCols.death[rk]]) : null,
        assist: roleMetricCols.assist[rk] >= 0 ? toStatNum(row[roleMetricCols.assist[rk]]) : null,
      };
      if (
        stat.gpm != null ||
        stat.dpm != null ||
        stat.kill != null ||
        stat.death != null ||
        stat.assist != null
      ) {
        roleStats[rk] = stat;
      }
    });

    out.push({
      team: teamCode,
      sheet: sheetName,
      sourceSheet: sheetName,
      sourceKind,
      date: ymd,
      week: idx.week >= 0 ? String(row[idx.week] ?? "").trim() : "",
      game: idx.game >= 0 ? String(row[idx.game] ?? "").trim() : "",
      competitor,
      side: idx.side >= 0 ? String(row[idx.side] ?? "").trim() : "",
      type: String(typeRaw ?? "").trim(),
      bucket: bucketFromType(typeRaw),
      matchWinRaw: idx.matchWin >= 0 ? row[idx.matchWin] : "",
      result: isWin == null ? "Unknown" : (isWin ? "Win" : "Lose"),
      win: isWin == null ? 0 : (isWin ? 1 : 0),
      lose: isWin == null ? 0 : (isWin ? 0 : 1),
      firstBloodWon,
      endType: idx.endType >= 0 ? String(row[idx.endType] ?? "").trim() : "",
      bans,
      picks,
      positions,
      opBans,
      opPicks,
      roleStats,
    });
  }

  return { rows: out, dropped, parsed: out.length, headerRow, droppedType };
}

function rowKey(r) {
  return [
    r.team || "",
    r.date || "",
    r.week || "",
    r.game || "",
    r.competitor || "",
    r.bucket || "",
  ].join("|");
}

function rowRichness(r) {
  return (r.picks?.length || 0) + (r.bans?.length || 0) + (r.opPicks?.length || 0) + (r.opBans?.length || 0);
}

function dedupeRows(rows) {
  const m = new Map();
  let duplicateDropped = 0;
  for (const r of rows || []) {
    const k = rowKey(r);
    const cur = m.get(k);
    if (!cur) {
      m.set(k, r);
      continue;
    }
    duplicateDropped += 1;
    if (rowRichness(r) > rowRichness(cur)) m.set(k, r);
  }
  return { rows: [...m.values()], duplicateDropped };
}

function buildCoverage(rawRows) {
  const byTeam = new Map();
  const bySource = new Map();

  for (const r of rawRows || []) {
    const t = r.team || "UNKNOWN";
    const bucket = r.bucket || "other";
    const src = r.sourceKind || "unknown";

    const teamCur = byTeam.get(t) || { team: t, scrim: 0, official: 0, other: 0, total: 0 };
    if (bucket === "scrim") teamCur.scrim += 1;
    else if (bucket === "official") teamCur.official += 1;
    else teamCur.other += 1;
    teamCur.total += 1;
    byTeam.set(t, teamCur);

    const srcCur = bySource.get(src) || { sourceKind: src, rows: 0 };
    srcCur.rows += 1;
    bySource.set(src, srcCur);
  }

  return {
    byTeam: [...byTeam.values()].sort((a, b) => a.team.localeCompare(b.team)),
    bySource: [...bySource.values()].sort((a, b) => b.rows - a.rows),
  };
}

export function deriveTeamPerformanceFromRaw(rawRows, options = {}) {
  const { includeOther = false, minGamesForDelta = 3 } = options;
  const agg = aggregateTeamPerformance(rawRows || [], { includeOther });
  const delta = buildScrimOfficialDelta(agg.heroSummary, { minGames: minGamesForDelta });
  return { agg, delta };
}

function aggregateTeamPerformance(rawRows, options = {}) {
  const { includeOther = false } = options;
  const rows = includeOther ? rawRows : rawRows.filter((r) => r.bucket === "scrim" || r.bucket === "official");

  const teamTotals = new Map();
  const heroAgg = new Map();
  const roleAgg = new Map();
  const roleMetricAgg = new Map();
  const teamFirstBloodAgg = new Map();

  const touchTeam = (team, bucket, win, lose) => {
    const key = `${team}|${bucket}`;
    const cur = teamTotals.get(key) || { team, bucket, games: 0, win: 0, lose: 0 };
    cur.games += 1;
    cur.win += win;
    cur.lose += lose;
    teamTotals.set(key, cur);
  };

  for (const r of rows) {
    touchTeam(r.team, r.bucket, r.win, r.lose);

    const teamKey = `${r.team}|${r.bucket}`;
    const fbKey = `${r.team}|${r.bucket}`;
    const fbCur = teamFirstBloodAgg.get(fbKey) || { team: r.team, bucket: r.bucket, games: 0, firstBlood: 0 };
    fbCur.games += 1;
    if (r.firstBloodWon === true) fbCur.firstBlood += 1;
    teamFirstBloodAgg.set(fbKey, fbCur);

    for (const h of r.picks || []) {
      const k = `${teamKey}|${h}`;
      const cur = heroAgg.get(k) || { team: r.team, bucket: r.bucket, hero: h, pick: 0, ban: 0, win: 0, lose: 0 };
      cur.pick += 1;
      cur.win += r.win;
      cur.lose += r.lose;
      heroAgg.set(k, cur);
    }

    for (const h of r.bans || []) {
      const k = `${teamKey}|${h}`;
      const cur = heroAgg.get(k) || { team: r.team, bucket: r.bucket, hero: h, pick: 0, ban: 0, win: 0, lose: 0 };
      cur.ban += 1;
      heroAgg.set(k, cur);
    }

    for (let i = 0; i < (r.picks || []).length; i++) {
      const hero = r.picks[i];
      const role = normalizeRole((r.positions || [])[i]);
      if (!hero || !role) continue;

      const k = `${teamKey}|${role}|${hero}`;
      const cur = roleAgg.get(k) || { team: r.team, bucket: r.bucket, role, hero, pick: 0, win: 0, lose: 0 };
      cur.pick += 1;
      cur.win += r.win;
      cur.lose += r.lose;
      roleAgg.set(k, cur);
    }

    ROLE_KEYS.forEach((rk) => {
      const rs = r.roleStats?.[rk];
      if (!rs) return;
      const k = `${teamKey}|${rk}`;
      const cur = roleMetricAgg.get(k) || {
        team: r.team,
        bucket: r.bucket,
        role: rk,
        rows: 0,
        gpmSum: 0,
        gpmN: 0,
        dpmSum: 0,
        dpmN: 0,
        killSum: 0,
        killN: 0,
        deathSum: 0,
        deathN: 0,
        assistSum: 0,
        assistN: 0,
      };
      cur.rows += 1;
      if (Number.isFinite(rs.gpm)) { cur.gpmSum += rs.gpm; cur.gpmN += 1; }
      if (Number.isFinite(rs.dpm)) { cur.dpmSum += rs.dpm; cur.dpmN += 1; }
      if (Number.isFinite(rs.kill)) { cur.killSum += rs.kill; cur.killN += 1; }
      if (Number.isFinite(rs.death)) { cur.deathSum += rs.death; cur.deathN += 1; }
      if (Number.isFinite(rs.assist)) { cur.assistSum += rs.assist; cur.assistN += 1; }
      roleMetricAgg.set(k, cur);
    });
  }

  const teamSummary = [...teamTotals.values()]
    .map((x) => ({ ...x, winRate: pct(x.win, x.games) }))
    .sort((a, b) => a.team.localeCompare(b.team) || a.bucket.localeCompare(b.bucket));

  const teamGamesMap = new Map(teamSummary.map((x) => [`${x.team}|${x.bucket}`, x.games]));

  const heroSummary = [...heroAgg.values()]
    .map((x) => {
      const games = safeNum(teamGamesMap.get(`${x.team}|${x.bucket}`), 0);
      const presence = x.pick + x.ban;
      return {
        ...x,
        games,
        presence,
        pickRate: pct(x.pick, games),
        banRate: pct(x.ban, games),
        presenceRate: pct(presence, games),
        winRate: pct(x.win, x.pick),
      };
    })
    .sort((a, b) => a.team.localeCompare(b.team) || a.bucket.localeCompare(b.bucket) || b.pick - a.pick);

  const rolePool = [...roleAgg.values()]
    .map((x) => {
      const games = safeNum(teamGamesMap.get(`${x.team}|${x.bucket}`), 0);
      return {
        ...x,
        games,
        pickRate: pct(x.pick, games),
        winRate: pct(x.win, x.pick),
      };
    })
    .sort((a, b) => a.team.localeCompare(b.team) || a.bucket.localeCompare(b.bucket) || a.role.localeCompare(b.role) || b.pick - a.pick);

  const roleMetrics = [...roleMetricAgg.values()]
    .map((x) => {
      const gpm = x.gpmN > 0 ? +(x.gpmSum / x.gpmN).toFixed(2) : null;
      const dpm = x.dpmN > 0 ? +(x.dpmSum / x.dpmN).toFixed(2) : null;
      const kill = x.killN > 0 ? +(x.killSum / x.killN).toFixed(2) : null;
      const death = x.deathN > 0 ? +(x.deathSum / x.deathN).toFixed(2) : null;
      const assist = x.assistN > 0 ? +(x.assistSum / x.assistN).toFixed(2) : null;
      const kda = (kill != null && death != null && assist != null)
        ? +(((kill + assist) / Math.max(1, death)).toFixed(2))
        : null;
      return { team: x.team, bucket: x.bucket, role: x.role, rows: x.rows, gpm, dpm, kill, death, assist, kda };
    })
    .sort((a, b) => a.team.localeCompare(b.team) || a.bucket.localeCompare(b.bucket) || a.role.localeCompare(b.role));

  const teamFirstBlood = [...teamFirstBloodAgg.values()]
    .map((x) => ({
      ...x,
      firstBloodRate: pct(x.firstBlood, x.games),
    }))
    .sort((a, b) => a.team.localeCompare(b.team) || a.bucket.localeCompare(b.bucket));

  return { teamSummary, heroSummary, rolePool, roleMetrics, teamFirstBlood };
}

function buildScrimOfficialDelta(heroSummary, options = {}) {
  const { minGames = 3 } = options;
  const byTeamHero = new Map();

  for (const h of heroSummary) {
    const key = `${h.team}|${h.hero}`;
    const cur = byTeamHero.get(key) || { team: h.team, hero: h.hero, scrim: null, official: null };
    if (h.bucket === "scrim") cur.scrim = h;
    if (h.bucket === "official") cur.official = h;
    byTeamHero.set(key, cur);
  }

  return [...byTeamHero.values()]
    .filter((x) => x.scrim && x.official)
    .filter((x) => x.scrim.games >= minGames && x.official.games >= minGames)
    .map((x) => ({
      team: x.team,
      hero: x.hero,
      scrimPickRate: x.scrim.pickRate,
      officialPickRate: x.official.pickRate,
      deltaPickRate: +(x.official.pickRate - x.scrim.pickRate).toFixed(2),
      scrimWinRate: x.scrim.winRate,
      officialWinRate: x.official.winRate,
      deltaWinRate: +(x.official.winRate - x.scrim.winRate).toFixed(2),
      scrimPresenceRate: x.scrim.presenceRate,
      officialPresenceRate: x.official.presenceRate,
      deltaPresenceRate: +(x.official.presenceRate - x.scrim.presenceRate).toFixed(2),
      scrimPick: x.scrim.pick,
      officialPick: x.official.pick,
      scrimGames: x.scrim.games,
      officialGames: x.official.games,
    }))
    .sort((a, b) => Math.abs(b.deltaWinRate) - Math.abs(a.deltaWinRate));
}

export async function parseTeamPerformanceFromWorkbook(input, options = {}) {
  const {
    sheets = [],
    includeOther = false,
    minGamesForDelta = 3,
  } = options;

  const wb = _toWorkbook(input);
  const teamSheets = detectTeamSheets(wb, sheets);
  const knownTeams = collectKnownTeamCodes(wb, teamSheets);

  const parseStats = [];
  const raw = [];

  teamSheets.forEach((sheetName) => {
    const parsed = parseOneTeamSheet(wb, sheetName, knownTeams);
    parseStats.push({
      sheet: sheetName,
      parsedRows: parsed.parsed,
      droppedRows: parsed.dropped,
      droppedTypeRows: parsed.droppedType || 0,
      headerRow: parsed.headerRow,
    });
    raw.push(...parsed.rows);
  });

  const deduped = dedupeRows(raw);
  const dedupedRaw = deduped.rows;
  const agg = aggregateTeamPerformance(dedupedRaw, { includeOther });
  const delta = buildScrimOfficialDelta(agg.heroSummary, { minGames: minGamesForDelta });
  const coverage = buildCoverage(dedupedRaw);

  return {
    meta: {
      teamSheets,
      totalRows: dedupedRaw.length,
      duplicateDropped: deduped.duplicateDropped,
      parseStats,
      coverage,
    },
    raw: dedupedRaw,
    agg,
    delta,
  };
}

export async function parseTeamPerformanceFromFile(file, options = {}) {
  const buf = await file.arrayBuffer();
  return parseTeamPerformanceFromWorkbook(buf, options);
}

export function buildRolePoolRecommendations(rolePool, options = {}) {
  const { minPick = 3, hotThreshold = 55, weakThreshold = 45 } = options;

  return (rolePool || [])
    .filter((r) => r.bucket === "official")
    .filter((r) => r.pick >= minPick)
    .map((r) => {
      let label = "Situational";
      if (r.winRate >= hotThreshold) label = "Core";
      else if (r.winRate <= weakThreshold) label = "Weak";
      return {
        team: r.team,
        role: r.role,
        hero: r.hero,
        pick: r.pick,
        winRate: r.winRate,
        label,
      };
    })
    .sort((a, b) => a.team.localeCompare(b.team) || a.role.localeCompare(b.role) || b.pick - a.pick);
}

export function summarizeTeamPriority(deltaRows, options = {}) {
  const { winGapThreshold = 8, pickGapThreshold = 5 } = options;
  const out = new Map();

  for (const d of deltaRows || []) {
    const key = d.team;
    const cur = out.get(key) || { team: d.team, trainMore: 0, stageReady: 0, unstable: 0 };

    if (d.deltaWinRate <= -winGapThreshold && d.deltaPickRate <= -pickGapThreshold) {
      cur.trainMore += 1;
    } else if (d.deltaWinRate >= winGapThreshold && d.deltaPickRate >= pickGapThreshold) {
      cur.stageReady += 1;
    } else {
      cur.unstable += 1;
    }

    out.set(key, cur);
  }

  return [...out.values()].sort((a, b) => b.trainMore - a.trainMore || b.unstable - a.unstable);
}

// =========================================================
//   Cross-Team Hero Comparison
//   เทียบ Hero ตัวเดียวกัน ข้ามทีม (Scrim vs Match Day)
// =========================================================
export function buildCrossTeamHeroComparison(heroSummary, options = {}) {
  const { minPick = 2 } = options;

  // Group by hero → teams
  const byHero = new Map();

  for (const h of heroSummary || []) {
    if (h.pick < minPick) continue;
    const key = h.hero;
    if (!byHero.has(key)) byHero.set(key, new Map());
    const teamMap = byHero.get(key);

    const tKey = h.team;
    if (!teamMap.has(tKey)) teamMap.set(tKey, { team: h.team, scrim: null, official: null });
    const entry = teamMap.get(tKey);

    if (h.bucket === "scrim") entry.scrim = h;
    if (h.bucket === "official") entry.official = h;
  }

  const results = [];

  for (const [hero, teamMap] of byHero) {
    const teams = [];
    for (const [, entry] of teamMap) {
      const s = entry.scrim;
      const o = entry.official;
      if (!s && !o) continue;

      teams.push({
        team: entry.team,
        scrimWR: s ? s.winRate : null,
        officialWR: o ? o.winRate : null,
        deltaWR: s && o ? +(o.winRate - s.winRate).toFixed(2) : null,
        scrimPR: s ? s.pickRate : null,
        officialPR: o ? o.pickRate : null,
        scrimPick: s ? s.pick : 0,
        officialPick: o ? o.pick : 0,
        scrimBanRate: s ? s.banRate : null,
        officialBanRate: o ? o.banRate : null,
      });
    }

    if (teams.length >= 1) {
      // Sort by total picks (most used first)
      teams.sort((a, b) => (b.scrimPick + b.officialPick) - (a.scrimPick + a.officialPick));
      const totalPicks = teams.reduce((acc, t) => acc + t.scrimPick + t.officialPick, 0);
      results.push({ hero, teams, totalPicks, teamCount: teams.length });
    }
  }

  // Sort by total usage across all teams
  return results.sort((a, b) => b.totalPicks - a.totalPicks);
}

// =========================================================
//   Hero Meta by Position
//   วิเคราะห์ Hero Meta ตามตำแหน่ง + สถานะ
// =========================================================
export function buildHeroMetaByPosition(rolePool, delta, options = {}) {
  const { minPick = 2, metaWR = 55, viableWR = 45, metaPresence = 30 } = options;

  const ROLES = ["DSL", "JUG", "MID", "ADL", "SUP"];
  const result = {};

  // Build delta lookup: team|hero → deltaWinRate
  const deltaMap = new Map();
  for (const d of delta || []) {
    deltaMap.set(`${d.team}|${d.hero}`, d);
  }

  for (const role of ROLES) {
    // Filter role pool for this position — official bucket
    const roleHeroes = (rolePool || [])
      .filter(r => r.role === role && r.bucket === "official" && r.pick >= minPick);

    // Aggregate same hero across teams
    const heroAgg = new Map();
    for (const r of roleHeroes) {
      const key = r.hero;
      const cur = heroAgg.get(key) || {
        hero: r.hero,
        totalPick: 0,
        totalWin: 0,
        totalLose: 0,
        totalGames: 0,
        teams: [],
        deltaWRs: [],
      };
      cur.totalPick += r.pick;
      cur.totalWin += r.win;
      cur.totalLose += r.lose;
      cur.totalGames += r.games;
      cur.teams.push(r.team);

      const dEntry = deltaMap.get(`${r.team}|${r.hero}`);
      if (dEntry) cur.deltaWRs.push(dEntry.deltaWinRate);

      heroAgg.set(key, cur);
    }

    // Calculate final stats
    const heroes = [...heroAgg.values()].map(h => {
      const wr = h.totalPick > 0 ? +((h.totalWin * 100) / h.totalPick).toFixed(2) : 0;
      const pr = h.totalGames > 0 ? +((h.totalPick * 100) / h.totalGames).toFixed(2) : 0;
      const avgDeltaWR = h.deltaWRs.length > 0
        ? +(h.deltaWRs.reduce((a, b) => a + b, 0) / h.deltaWRs.length).toFixed(2)
        : null;

      // Determine status
      let status, statusColor;
      if (wr >= metaWR && pr >= metaPresence) {
        status = "🔥 Meta";
        statusColor = "#ff1026";
      } else if (wr >= viableWR) {
        status = "✅ Viable";
        statusColor = "#00d084";
      } else if (wr >= 40) {
        status = "⚠️ Risky";
        statusColor = "#ffd166";
      } else {
        status = "❌ Avoid";
        statusColor = "#ff4d6d";
      }

      // Training recommendation
      let recommendation = "";
      if (avgDeltaWR !== null && avgDeltaWR < -10) {
        recommendation = "ซ้อมดีแต่แข่งไม่ดี — ต้องฝึกเพิ่ม";
      } else if (avgDeltaWR !== null && avgDeltaWR > 10) {
        recommendation = "แข่งจริงดีกว่าซ้อม — ใช้ต่อได้";
      } else if (status === "🔥 Meta") {
        recommendation = "Meta Pick — ต้องเล่นเป็น";
      } else if (status === "❌ Avoid") {
        recommendation = "WR ต่ำ — หลีกเลี่ยง";
      }

      return {
        hero: h.hero,
        pick: h.totalPick,
        win: h.totalWin,
        wr,
        pr,
        avgDeltaWR,
        teams: [...new Set(h.teams)],
        teamCount: [...new Set(h.teams)].length,
        status,
        statusColor,
        recommendation,
      };
    });

    // Sort: Meta first, then by pick rate
    heroes.sort((a, b) => {
      const order = { "🔥 Meta": 0, "✅ Viable": 1, "⚠️ Risky": 2, "❌ Avoid": 3 };
      const diff = (order[a.status] ?? 9) - (order[b.status] ?? 9);
      return diff !== 0 ? diff : b.pr - a.pr;
    });

    result[role] = heroes;
  }

  return result;
}

export function buildTeamDraftPreferences(rawRows, options = {}) {
  const { minGames = 1 } = options;

  const teamMap = new Map();
  const opponentMap = new Map();

  const touchHero = (store, hero, kind, winAdd = 0, loseAdd = 0) => {
    if (!hero) return;
    const cur = store.get(hero) || { hero, pick: 0, ban: 0, win: 0, lose: 0 };
    if (kind === "pick") {
      cur.pick += 1;
      cur.win += winAdd;
      cur.lose += loseAdd;
    } else if (kind === "ban") {
      cur.ban += 1;
    }
    store.set(hero, cur);
  };

  const touchTeam = (map, key) => {
    if (!map.has(key)) {
      map.set(key, { games: 0, heroes: new Map(), comps: new Map() });
    }
    return map.get(key);
  };

  for (const r of rawRows || []) {
    const team = normalizeTeamAlias(r.team);
    const opponent = normalizeTeamAlias(r.competitor);
    if (!team) continue;

    const teamStore = touchTeam(teamMap, team);
    teamStore.games += 1;

    (r.picks || []).forEach((h) => touchHero(teamStore.heroes, h, "pick", Number(r.win || 0), Number(r.lose || 0)));
    (r.bans || []).forEach((h) => touchHero(teamStore.heroes, h, "ban"));

    if ((r.picks || []).length > 0) {
      const roles = r.positions || [];
      const compKey = (r.picks || [])
        .map((h, i) => {
          const role = normalizeRole(roles[i] || "");
          return role ? `${role}:${h}` : h;
        })
        .join(" | ");
      if (compKey) {
        const compCur = teamStore.comps.get(compKey) || { comp: compKey, count: 0, win: 0, lose: 0 };
        compCur.count += 1;
        compCur.win += Number(r.win || 0);
        compCur.lose += Number(r.lose || 0);
        teamStore.comps.set(compKey, compCur);
      }
    }

    if (opponent) {
      const opStore = touchTeam(opponentMap, opponent);
      opStore.games += 1;
      (r.opPicks || []).forEach((h) => touchHero(opStore.heroes, h, "pick"));
      (r.opBans || []).forEach((h) => touchHero(opStore.heroes, h, "ban"));
    }
  }

  const finalizeHeroes = (heroMap, games) =>
    [...heroMap.values()]
      .map((h) => {
        const presence = h.pick + h.ban;
        return {
          ...h,
          presence,
          pickRate: pct(h.pick, games),
          banRate: pct(h.ban, games),
          presenceRate: pct(presence, games),
          winRate: pct(h.win, h.pick),
        };
      })
      .sort((a, b) => b.presence - a.presence || b.pick - a.pick || a.hero.localeCompare(b.hero));

  const byTeam = {};
  for (const [team, store] of teamMap.entries()) {
    if (store.games < minGames) continue;
    byTeam[team] = {
      team,
      games: store.games,
      heroes: finalizeHeroes(store.heroes, store.games),
      comps: [...store.comps.values()]
        .map((c) => ({ ...c, winRate: pct(c.win, c.count) }))
        .sort((a, b) => b.count - a.count || b.winRate - a.winRate),
    };
  }

  const byOpponent = {};
  for (const [op, store] of opponentMap.entries()) {
    if (store.games < minGames) continue;
    byOpponent[op] = {
      opponent: op,
      games: store.games,
      heroes: finalizeHeroes(store.heroes, store.games),
    };
  }

  return {
    byTeam,
    byOpponent,
    teams: Object.keys(byTeam).sort(),
    opponents: Object.keys(byOpponent).sort(),
  };
}

export function buildLeagueRoleBenchmarks(rawRows, options = {}) {
  const { bucket = "official", minSamples = 3 } = options;
  const rows = (rawRows || []).filter((r) => !bucket || r.bucket === bucket);
  const agg = aggregateTeamPerformance(rows, { includeOther: !bucket || bucket === "other" });

  const byRole = new Map();
  (agg.roleMetrics || []).forEach((r) => {
    if (r.bucket !== bucket) return;
    const cur = byRole.get(r.role) || {
      role: r.role,
      gpmSum: 0,
      gpmN: 0,
      dpmSum: 0,
      dpmN: 0,
      killSum: 0,
      killN: 0,
      deathSum: 0,
      deathN: 0,
      assistSum: 0,
      assistN: 0,
    };
    if (Number.isFinite(r.gpm)) { cur.gpmSum += r.gpm; cur.gpmN += 1; }
    if (Number.isFinite(r.dpm)) { cur.dpmSum += r.dpm; cur.dpmN += 1; }
    if (Number.isFinite(r.kill)) { cur.killSum += r.kill; cur.killN += 1; }
    if (Number.isFinite(r.death)) { cur.deathSum += r.death; cur.deathN += 1; }
    if (Number.isFinite(r.assist)) { cur.assistSum += r.assist; cur.assistN += 1; }
    byRole.set(r.role, cur);
  });

  const leagueRoleAvg = {};
  for (const rk of ROLE_KEYS) {
    const b = byRole.get(rk);
    if (!b) continue;
    const kill = b.killN > 0 ? b.killSum / b.killN : null;
    const death = b.deathN > 0 ? b.deathSum / b.deathN : null;
    const assist = b.assistN > 0 ? b.assistSum / b.assistN : null;
    leagueRoleAvg[rk] = {
      gpm: b.gpmN > 0 ? +(b.gpmSum / b.gpmN).toFixed(2) : null,
      dpm: b.dpmN > 0 ? +(b.dpmSum / b.dpmN).toFixed(2) : null,
      kill: kill != null ? +kill.toFixed(2) : null,
      death: death != null ? +death.toFixed(2) : null,
      assist: assist != null ? +assist.toFixed(2) : null,
      kda: (kill != null && death != null && assist != null) ? +(((kill + assist) / Math.max(1, death)).toFixed(2)) : null,
    };
  }

  const roleRows = (agg.roleMetrics || [])
    .filter((r) => r.bucket === bucket)
    .filter((r) => (r.rows || 0) >= minSamples)
    .map((r) => {
      const avg = leagueRoleAvg[r.role] || {};
      return {
        ...r,
        leagueGpm: avg.gpm ?? null,
        leagueDpm: avg.dpm ?? null,
        leagueKda: avg.kda ?? null,
        deltaGpm: Number.isFinite(r.gpm) && Number.isFinite(avg.gpm) ? +(r.gpm - avg.gpm).toFixed(2) : null,
        deltaDpm: Number.isFinite(r.dpm) && Number.isFinite(avg.dpm) ? +(r.dpm - avg.dpm).toFixed(2) : null,
        deltaKda: Number.isFinite(r.kda) && Number.isFinite(avg.kda) ? +(r.kda - avg.kda).toFixed(2) : null,
      };
    });

  const teamFirstBlood = (agg.teamFirstBlood || [])
    .filter((r) => r.bucket === bucket)
    .sort((a, b) => b.firstBloodRate - a.firstBloodRate);
  const leagueFirstBloodRate = teamFirstBlood.length
    ? +(teamFirstBlood.reduce((a, r) => a + Number(r.firstBloodRate || 0), 0) / teamFirstBlood.length).toFixed(2)
    : 0;
  const teamFirstBloodWithDelta = teamFirstBlood.map((r) => ({
    ...r,
    leagueFirstBloodRate,
    deltaFirstBloodRate: +(Number(r.firstBloodRate || 0) - leagueFirstBloodRate).toFixed(2),
  }));

  return {
    roleRows,
    leagueRoleAvg,
    teamFirstBlood: teamFirstBloodWithDelta,
    leagueFirstBloodRate,
  };
}
